/**
 * Routing System Constants
 * 
 * Single source of truth for routing behavior configuration.
 * These values enforce invariants across the routing system.
 */

// ============================================================================
// LANGUAGE SWITCH SAFEGUARDS (INV-5, INV-6)
// ============================================================================

/**
 * Maximum time (ms) a pending language switch can be active before expiring.
 * If the manifest doesn't load and navigate within this window, the switch is cancelled.
 */
export const LANGUAGE_SWITCH_TTL_MS = 5000;

// ============================================================================
// COLD ENTRY RESOLUTION (INV-1, INV-3)
// ============================================================================

/**
 * localStorage key for user's locale preference.
 * Used as highest priority in cold entry resolution.
 */
export const USER_LOCALE_STORAGE_KEY = 'user-locale';

/**
 * Default locale when all resolution methods fail.
 */
export const DEFAULT_LOCALE = 'en';

/**
 * Default regional locale (full code) when all resolution methods fail.
 */
export const DEFAULT_REGIONAL_LOCALE = 'en-US';

// ============================================================================
// ENTITY TYPES (INV-9)
// ============================================================================

/**
 * Entity types that change URLs on language switch.
 * These have locale-specific slugs in localization_metadata.
 */
export const URL_CHANGING_ENTITIES = ['route', 'page', 'location', 'home'] as const;

/**
 * Entity types that keep the same URL on language switch.
 * Content refetches instead of URL changing.
 */
export const STATIC_URL_ENTITIES = ['expert', 'dynamic'] as const;

// ============================================================================
// SOURCE TYPES FOR LOCALIZATION_METADATA (INV-2)
// ============================================================================

/**
 * Mapping from metadata source_type to entity type.
 */
/**
 * Functional route IDs that always use EN slugs regardless of locale.
 * These are system routes (not indexable content) — checkout, account, auth, plans.
 * They never override locale context and never get localized slugs in generated links.
 */
export const FUNCTIONAL_ROUTE_IDS = new Set(['account', 'auth', 'plans', 'checkout', 'admin']);

export const SOURCE_TYPE_TO_ENTITY: Record<string, string> = {
  'location': 'location',
  'route': 'route',
  'page': 'page',
  'publication': 'dynamic',
  'profile': 'expert',
  'topic': 'dynamic',
  'faq': 'dynamic',
} as const;
